/**
 * src/utils/attendanceCalculation.js
 * 
 * Complete helper for evaluating employee monthly attendance, leaves,
 * half-day attendance, and calculating applicable Attendance / LOP deductions.
 */

/**
 * Returns total days and Mon-Fri working days in a given month and year.
 */
export function getMonthDaysInfo(year, month) {
  const y = Number(year);
  const m = Number(month); // 1-12
  const totalDays = new Date(y, m, 0).getDate();

  let workingDays = 0;
  for (let d = 1; d <= totalDays; d++) {
    const dayOfWeek = new Date(y, m - 1, d).getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude Sun (0) & Sat (6)
      workingDays++;
    }
  }

  return { totalDays, workingDays: workingDays > 0 ? workingDays : totalDays };
}

/**
 * Safely normalizes date values (Date, Timestamp, string) to YYYY-MM-DD
 */
export function normalizeDateStr(dateVal) {
  if (!dateVal) return "";
  if (typeof dateVal === "string") return dateVal.trim();
  if (typeof dateVal.toDate === "function") {
    const d = dateVal.toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  if (dateVal instanceof Date && !Number.isNaN(dateVal.getTime())) {
    return `${dateVal.getFullYear()}-${String(dateVal.getMonth() + 1).padStart(2, "0")}-${String(dateVal.getDate()).padStart(2, "0")}`;
  }
  if (typeof dateVal.seconds === "number") {
    const d = new Date(dateVal.seconds * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  return String(dateVal).trim();
}

/**
 * Check if a given attendance record qualifies as a Half-Day.
 */
export function isRecordHalfDay(att) {
  if (!att) return false;

  const status = (att.status || "").toString().toLowerCase().trim();
  const checkoutStatus = (att.checkoutStatus || "").toString().toLowerCase().trim();

  if (
    status === "half day" ||
    status === "half-day" ||
    status === "half_day" ||
    checkoutStatus === "half day" ||
    checkoutStatus === "half-day" ||
    checkoutStatus === "half_day" ||
    att.isHalfDay === true
  ) {
    return true;
  }

  // Check productive / hours worked (< 4 hrs on a checked-out day)
  const hours = Number(att.productiveHours ?? att.hoursWorked ?? 0);
  const isCheckedOut = Boolean(att.checkOut || status === "checked-out");
  if (isCheckedOut && hours > 0 && hours < 4) {
    return true;
  }

  return false;
}

/**
 * Check if a leave request is an approved PAID leave (not LOP).
 */
export function isPaidLeave(leave) {
  if (!leave) return false;
  const status = (leave.status || "").toString().toLowerCase().trim();
  if (status !== "approved") return false;

  const type = (leave.leaveType || leave.actualLeaveType || leave.deductFrom || "").toString().toLowerCase().trim();
  if (
    type === "unpaid" ||
    type === "lop" ||
    type === "loss_of_pay" ||
    type === "loss of pay" ||
    type === "unpaid_leave"
  ) {
    return false;
  }
  return true;
}

/**
 * Calculate comprehensive attendance and LOP deductions for a specific employee in a given month/year.
 * 
 * @param {Object} params
 * @param {string|Array} params.employeeId - Employee User ID / Employee ID
 * @param {Array} [params.candidateIds] - Multiple candidate IDs (uid, employeeId, customId)
 * @param {number} params.month - Month number (1 - 12)
 * @param {number} params.year - Year (e.g. 2026)
 * @param {number} params.monthlyGross - Total monthly earnings/gross salary
 * @param {number} [params.salaryDays=30] - Effective salary days divisor (default 30)
 * @param {Array} [params.attendanceList=[]] - Attendance records collection
 * @param {Array} [params.leavesList=[]] - Leave requests collection
 * @param {Array} [params.holidaysList=[]] - Holidays collection
 * 
 * @returns {Object} Attendance & LOP breakdown
 */
export function calculateEmployeeAttendanceAndLop({
  employeeId,
  candidateIds = [],
  month,
  year,
  monthlyGross = 0,
  salaryDays = 30,
  attendanceList = [],
  leavesList = [],
  holidaysList = [],
}) {
  const allIds = [
    ...(Array.isArray(candidateIds) ? candidateIds : [candidateIds]),
    ...(Array.isArray(employeeId) ? employeeId : [employeeId]),
  ].filter(Boolean).map((id) => String(id).trim());

  if (allIds.length === 0) {
    return {
      totalMonthDays: 30,
      totalWorkingDays: 22,
      presentDays: 0,
      paidLeaveDays: 0,
      unpaidLeaveDays: 0,
      halfDayCount: 0,
      absentDays: 0,
      workedDays: 30,
      perDaySalary: 0,
      fullDayLopDeduction: 0,
      halfDayDeduction: 0,
      totalAttendanceLopDeduction: 0,
      halfDayDates: [],
      unpaidDates: [],
      absentDates: [],
    };
  }

  const idSet = new Set(allIds);

  const mNum = Number(month);
  const yNum = Number(year);
  const monthStr = String(mNum).padStart(2, "0");
  const prefix = `${yNum}-${monthStr}`;

  const { totalDays, workingDays } = getMonthDaysInfo(yNum, mNum);

  // 1. Filter attendance records for this employee in the selected month
  const userAtt = (attendanceList || []).filter((a) => {
    const aUserId = a.userId ? String(a.userId).trim() : "";
    const aEmpId = a.employeeId ? String(a.employeeId).trim() : "";
    const aUid = a.uid ? String(a.uid).trim() : "";
    const aDocId = a.id ? String(a.id).trim() : "";

    const matchUser =
      (aUserId && idSet.has(aUserId)) ||
      (aEmpId && idSet.has(aEmpId)) ||
      (aUid && idSet.has(aUid)) ||
      allIds.some((id) => aDocId.startsWith(`${id}_`));

    const dateStr = normalizeDateStr(a.date);
    const matchDate = dateStr.startsWith(prefix) || dateStr.startsWith(`${yNum}-${mNum}-`);
    return matchUser && matchDate;
  });

  const attByDate = new Map();
  userAtt.forEach((a) => {
    const dStr = normalizeDateStr(a.date);
    if (dStr) attByDate.set(dStr, a);
  });

  // 2. Filter approved leaves for this employee in the selected month
  const userLeaves = (leavesList || []).filter((l) => {
    const lUserId = l.userId ? String(l.userId).trim() : "";
    const lEmpId = l.employeeId ? String(l.employeeId).trim() : "";
    const lUid = l.uid ? String(l.uid).trim() : "";

    const matchUser =
      (lUserId && idSet.has(lUserId)) ||
      (lEmpId && idSet.has(lEmpId)) ||
      (lUid && idSet.has(lUid));

    const isApproved = (l.status || "").toString().toLowerCase().trim() === "approved";
    const fromStr = normalizeDateStr(l.fromDate || l.date || l.startDate);
    const toStr = normalizeDateStr(l.toDate || l.fromDate || l.date || l.endDate);
    const dateOverlap = (fromStr && fromStr.startsWith(prefix)) || (toStr && toStr.startsWith(prefix));

    return matchUser && isApproved && dateOverlap;
  });

  const holidayDates = new Set(
    (holidaysList || [])
      .filter((h) => {
        const hDate = normalizeDateStr(h.date);
        return hDate.startsWith(prefix) && !h.isOptional;
      })
      .map((h) => normalizeDateStr(h.date))
  );

  const paidLeaveDates = new Set();
  const unpaidLeaveFullDates = new Set();
  const unpaidLeaveHalfDates = new Set();

  userLeaves.forEach((l) => {
    const isPaid = isPaidLeave(l);
    const from = normalizeDateStr(l.fromDate || l.date || l.startDate);
    const to = normalizeDateStr(l.toDate || l.fromDate || l.date || l.endDate);
    if (!from) return;

    const startDate = new Date(from);
    const endDate = new Date(to || from);

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (dateKey.startsWith(prefix)) {
        if (isPaid) {
          paidLeaveDates.add(dateKey);
        } else if (l.isHalfDay || l.leaveType === "half_day") {
          unpaidLeaveHalfDates.add(dateKey);
        } else {
          unpaidLeaveFullDates.add(dateKey);
        }
      }
    }
  });

  const presentDates = new Set();
  const halfDayDates = new Set();
  const absentDates = new Set();

  // Evaluate explicit records first
  userAtt.forEach((a) => {
    const dateKey = normalizeDateStr(a.date);
    if (!dateKey) return;

    const status = (a.status || "").toString().toLowerCase().trim();
    const isAbsentStatus =
      status === "absent" ||
      a.isAbsent === true ||
      a.attendanceStatus === "absent";

    if (isAbsentStatus) {
      if (!paidLeaveDates.has(dateKey)) {
        absentDates.add(dateKey);
      }
      return;
    }

    const hasCheckedIn = Boolean(
      a.checkIn ||
      status === "present" ||
      status === "checked-out" ||
      status === "active" ||
      status === "half day" ||
      status === "half-day" ||
      a.isHalfDay === true
    );

    if (hasCheckedIn) {
      if (isRecordHalfDay(a)) {
        if (!paidLeaveDates.has(dateKey)) {
          halfDayDates.add(dateKey);
        } else {
          presentDates.add(dateKey); // Covered by paid leave
        }
      } else {
        presentDates.add(dateKey);
      }
    }
  });

  // Add unpaid leave dates to half-days and full-day absents
  unpaidLeaveHalfDates.forEach((d) => {
    if (!paidLeaveDates.has(d) && !halfDayDates.has(d) && !presentDates.has(d)) {
      halfDayDates.add(d);
    }
  });

  unpaidLeaveFullDates.forEach((d) => {
    if (!paidLeaveDates.has(d) && !presentDates.has(d)) {
      absentDates.add(d);
    }
  });

  // Check working days (Mon-Fri) for unexcused absences
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const isCurrentMonth = today.getFullYear() === yNum && today.getMonth() + 1 === mNum;
  const lastDayToCheck = isCurrentMonth ? today.getDate() : totalDays;

  // Scan working days up to last day
  for (let day = 1; day <= lastDayToCheck; day++) {
    const dd = String(day).padStart(2, "0");
    const dateStr = `${yNum}-${monthStr}-${dd}`;
    const dayOfWeek = new Date(yNum, mNum - 1, day).getDay();

    // Check working days only (Mon-Fri) and skip holidays
    if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dateStr)) {
      const hasAtt = attByDate.has(dateStr);
      const isPresentDate = presentDates.has(dateStr);
      const isHalfDayDate = halfDayDates.has(dateStr);
      const isPaidLeaveDate = paidLeaveDates.has(dateStr);
      const isAbsentDate = absentDates.has(dateStr);

      if (!hasAtt && !isPresentDate && !isHalfDayDate && !isPaidLeaveDate && !isAbsentDate) {
        if (dateStr <= todayStr || !isCurrentMonth) {
          absentDates.add(dateStr);
        }
      }
    }
  }

  const presentDays = presentDates.size;
  const paidLeaveDays = paidLeaveDates.size;
  const unpaidLeaveDays = unpaidLeaveFullDates.size;
  const halfDayCount = halfDayDates.size;
  const absentDays = absentDates.size;

  // Effective divisor for salary calculations (default 30 days)
  const effectiveSalaryDays = Number(salaryDays) > 0 ? Number(salaryDays) : 30;
  const perDaySalary = effectiveSalaryDays > 0 ? Number(monthlyGross || 0) / effectiveSalaryDays : 0;

  // Deductions:
  // 1. Absent/LOP Days: @100% per-day
  const fullDayLopDeduction = Math.round(absentDays * perDaySalary);

  // 2. Unpaid Half-Days: @50% per-day
  const halfDayDeduction = Math.round(halfDayCount * (perDaySalary * 0.5));

  // 3. Total Attendance / LOP Deduction
  const totalAttendanceLopDeduction = fullDayLopDeduction + halfDayDeduction;

  // Worked Days Calculation:
  // Derived from monthly attendance: 30 standard days minus full absent days minus half-day deduction (0.5 per half day)
  // or presentDays + paidLeaveDays + (halfDayCount * 0.5)
  let workedDays = 30;
  if (absentDays > 0 || halfDayCount > 0) {
    workedDays = Math.max(0, Math.round((30 - absentDays - (halfDayCount * 0.5)) * 10) / 10);
  } else if (presentDays > 0) {
    workedDays = Math.min(30, Math.round((presentDays + paidLeaveDays + (halfDayCount * 0.5)) * 10) / 10);
  }

  return {
    totalMonthDays: totalDays,
    totalWorkingDays: workingDays,
    presentDays,
    paidLeaveDays,
    unpaidLeaveDays,
    halfDayCount,
    absentDays,
    workedDays,
    perDaySalary: Math.round(perDaySalary * 100) / 100,
    fullDayLopDeduction,
    halfDayDeduction,
    totalAttendanceLopDeduction,
    halfDayDates: Array.from(halfDayDates),
    unpaidDates: Array.from(unpaidLeaveFullDates),
    absentDates: Array.from(absentDates),
  };
}
